import { useParams, useLocation, Link } from "react-router-dom";
import { useState, useEffect, useMemo, useRef, useCallback, useContext } from "react";
import { db } from "../firebase/config";
import { logUsageEvent } from "../utils/usageAnalytics";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  getDoc,
  deleteDoc
} from "firebase/firestore";
import { useDrag, useDragLayer, useDrop } from "react-dnd";
import { DEFAULT_LAYOUT } from "../defaultLayout";
import { serverTimestamp, addDoc, increment } from "firebase/firestore";
import { buttonStyles, inputStyles } from "../styles/ui";
import { AuthContext } from "../AuthContext";
import {
  GUEST_CLASSES,
  GUEST_DESK_LAYOUTS,
  GUEST_EVENTS,
  GUEST_OBJECTIVES,
  GUEST_STUDENTS,
} from "../utils/guestDummyData";


// ----------------------
// Constants & helpers
// ----------------------

const GRID_SIZE = 40;
const DESK_WIDTH = 120;
const DESK_HEIGHT = 80;
const DESK_START_X = 40;
const DESK_START_Y = 40;
const DESK_MAX_X = 1200;
const DESK_MAX_Y = 900;

const snapToGrid = (x, y) => {
  const snappedX = Math.round(x / GRID_SIZE) * GRID_SIZE;
  const snappedY = Math.round(y / GRID_SIZE) * GRID_SIZE;
  return { x: snappedX, y: snappedY };
};

const isPositionFree = (position, positions, ignoreId) => {
  for (const [otherId, otherPos] of Object.entries(positions)) {
    if (otherId === ignoreId) continue;
    if (desksOverlap(position, otherPos)) return false;
  }
  return true;
};

const findNearestOpenPosition = (start, positions, ignoreId) => {
  if (isPositionFree(start, positions, ignoreId)) return start;

  const desks = Object.values(positions || {});
  const maxDeskX = desks.reduce((max, desk) => Math.max(max, (desk.x || 0) + DESK_WIDTH), 0);
  const maxDeskY = desks.reduce((max, desk) => Math.max(max, (desk.y || 0) + DESK_HEIGHT), 0);
  const buffer = GRID_SIZE * 6;
  const maxX = Math.max(maxDeskX, start.x + DESK_WIDTH) + buffer;
  const maxY = Math.max(maxDeskY, start.y + DESK_HEIGHT) + buffer;

  const maxXSteps = Math.ceil((maxX - DESK_START_X) / GRID_SIZE);
  const maxYSteps = Math.ceil((maxY - DESK_START_Y) / GRID_SIZE);
  const maxRadius = Math.max(maxXSteps, maxYSteps);

  for (let radius = 1; radius <= maxRadius; radius += 1) {
    for (let dx = -radius; dx <= radius; dx += 1) {
      for (let dy = -radius; dy <= radius; dy += 1) {
        if (Math.abs(dx) !== radius && Math.abs(dy) !== radius) continue;
        const candidate = {
          x: start.x + dx * GRID_SIZE,
          y: start.y + dy * GRID_SIZE,
        };
        if (candidate.x < DESK_START_X || candidate.y < DESK_START_Y) {
          continue;
        }
        if (isPositionFree(candidate, positions, ignoreId)) {
          return candidate;
        }
      }
    }
  }

  return null;
};

const desksOverlap = (a, b) => {
  const width = 120;
  const height = 80;

  return !(
    a.x + width <= b.x ||
    a.x >= b.x + width ||
    a.y + height <= b.y ||
    a.y >= b.y + height
  );
};

const getDeskNumber = (deskId) => {
  const match = String(deskId).match(/\d+/);
  return match ? Number(match[0]) : 0;
};

const getNextDeskId = (positions) => {
  const maxId = Object.keys(positions)
    .map(getDeskNumber)
    .reduce((max, current) => Math.max(max, current), 0);
  return `desk${maxId + 1}`;
};

const findOpenDeskPosition = (positions) => {
  for (let y = DESK_START_Y; y <= DESK_MAX_Y; y += GRID_SIZE) {
    for (let x = DESK_START_X; x <= DESK_MAX_X; x += GRID_SIZE) {
      const candidate = { x, y };
      const hasOverlap = Object.values(positions).some((pos) =>
        desksOverlap(candidate, pos)
      );
      if (!hasOverlap) return candidate;
    }
  }
  return { x: DESK_START_X, y: DESK_START_Y };
};


// ----------------------
// Desk component
// ----------------------

export function Desk({
  deskId,
  x,
  y,
  rotation,
  onMove,
  onRotate,
  onDropStudent,
  assignedStudent,
  teachingMode,
  selected,
  onSelectStudent,
  highlightColour,
  stats,
  scale = 1
}) {
  // Drag/drop hooks still defined, but refs only attached when not in teachingMode
  const [{ isDragging }, dragRef] = useDrag(
    () => ({
      type: "DESK",
      item: { deskId, x, y },
      collect: (monitor) => ({
        isDragging: monitor.isDragging()
      })
    }),
    [teachingMode, deskId, x, y]
  );

  const [{ isOver }, dropRef] = useDrop(
    () => ({
      accept: "STUDENT",
      drop: (student) => onDropStudent(deskId, student),
      collect: (monitor) => ({
        isOver: monitor.isOver()
      })
    }),
    [teachingMode]
  );

  // TM1: conditional refs – only attach drag/drop when NOT in teaching mode
  const combinedRef = (node) => {
    if (!teachingMode && !highlightColour) {
      dragRef(node);
      dropRef(node);
    }
  };

  const handleRightClick = (e) => {
    if (teachingMode || highlightColour) return;
    e.preventDefault();
    onRotate(deskId);
  };

  const handleClick = () => {
    if (highlightColour) return;
    if (teachingMode && assignedStudent) {
      onSelectStudent(assignedStudent.id);
    }
  };

  const normalizeFlag = (value) => String(value || "").trim().toUpperCase();
  const hasSen = normalizeFlag(assignedStudent?.senStatus) === "K";
  const hasPp = normalizeFlag(assignedStudent?.pupilPremiumStatus) === "Y";

  return (
    <div
      ref={combinedRef}
      onContextMenu={handleRightClick}
      onClick={handleClick}
      style={{
        position: "absolute",
        left: x * scale,
        top: y * scale,
        width: `${120 * scale}px`,
        height: `${80 * scale}px`,
        border: `${Math.max(1, Math.round(2 * scale))}px solid #444`,
        borderRadius: "6px",
background: highlightColour
  ? highlightColour
  : isOver
    ? "#d0f0d0"
    : "#fafafa",
        opacity: teachingMode ? 1 : isDragging ? 0.5 : 1,
        cursor: teachingMode ? "pointer" : "move",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transform: `rotate(${rotation || 0}deg)`,
        transformOrigin: "center center",
        boxShadow: selected ? "0 0 10px 3px #4da3ff" : "none"
      }}
    >
      {assignedStudent ? (
        <div style={{ textAlign: "center" }}>
          <div>{assignedStudent.name}</div>
          {(hasSen || hasPp) && (
            <div
              style={{
                display: "flex",
                gap: "6px",
                justifyContent: "center",
                marginTop: "4px",
                flexWrap: "wrap"
              }}
            >
              {hasSen && (
                <span
                  style={{
                    background: "#1976d2",
                    color: "#fff",
                    fontSize: `${Math.max(10, Math.round(10 * scale))}px`,
                    padding: "2px 6px",
                    borderRadius: "999px",
                    fontWeight: 700
                  }}
                >
                  [SEN]
                </span>
              )}
              {hasPp && (
                <span
                  style={{
                    background: "#2e7d32",
                    color: "#fff",
                    fontSize: `${Math.max(10, Math.round(10 * scale))}px`,
                    padding: "2px 6px",
                    borderRadius: "999px",
                    fontWeight: 700
                  }}
                >
                  [PP]
                </span>
              )}
            </div>
          )}
        </div>
      ) : (
        deskId
      )}
    </div>
  );
}

// ----------------------
// Draggable student
// ----------------------

function DraggableStudent({ student, teachingMode, isAssigned }) {
  const [{ isDragging }, dragRef] = useDrag(
    () => ({
      type: "STUDENT",
      item: { id: student.id, name: student.name },
      collect: (monitor) => ({
        isDragging: monitor.isDragging()
      })
    }),
    [teachingMode]
  );

  return (
    <li
      ref={teachingMode ? null : dragRef}
      style={{
        opacity: isAssigned ? 0.5 : isDragging ? 0.5 : 1,
        cursor: teachingMode ? "default" : "grab",
        padding: "6px 10px",
        background: "#eee",
        marginBottom: "6px",
        borderRadius: "4px",
        listStyle: "none"
      }}
    >
      {student.name}
    </li>
  );
}

// ----------------------
// Main component
// ----------------------

export default function SeatingPlan() {
  const { id } = useParams(); // classId
  const { user, isGuest } = useContext(AuthContext);
  const location = useLocation();


  const [className, setClassName] = useState("");
  const [classTeacherId, setClassTeacherId] = useState(null);
  const [students, setStudents] = useState([]);
  const [deskPositions, setDeskPositions] = useState(
    () => DEFAULT_LAYOUT.desk || {}
  );
  const [deskAssignments, setDeskAssignments] = useState({});
  const [currentLayoutId, setCurrentLayoutId] = useState("default");
  const [savedLayouts, setSavedLayouts] = useState([]);
  const [layoutMenuOpen, setLayoutMenuOpen] = useState(false);
  const [layoutPreview, setLayoutPreview] = useState(null);
  const [layoutPreviewLabel, setLayoutPreviewLabel] = useState("");
  const [layoutPreviewLoading, setLayoutPreviewLoading] = useState(false);
  const deskAreaContainerRef = useRef(null);
  const deskAreaContentRef = useRef(null);
  const [deskAreaScale, setDeskAreaScale] = useState(1);
  const [deskAreaHeight, setDeskAreaHeight] = useState(null);
  const [userDefaultDesks, setUserDefaultDesks] = useState(null);

  // === Teaching Mode ===
  const [teachingMode, setTeachingMode] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [attendanceMode, setAttendanceMode] = useState(false);
  const [attendanceSaving, setAttendanceSaving] = useState(false);

 
 
  // === Learning Objectives Modal ===
  const [objectiveModalOpen, setObjectiveModalOpen] = useState(false);
  const [objectives, setObjectives] = useState([]);


  // === Learning Objective Manager ===
const [objectiveManagerOpen, setObjectiveManagerOpen] = useState(false);
const [objectiveEditorOpen, setObjectiveEditorOpen] = useState(false);
const [objectiveToEdit, setObjectiveToEdit] = useState(null);

//=== Load events ===
const [events, setEvents] = useState([]);

useEffect(() => {
  if (!teachingMode) return;

  const loadEvents = async () => {
    if (isGuest) {
      setEvents(GUEST_EVENTS[id] || []);
      return;
    }

    const eventsRef = collection(db, "classes", id, "events");
    const snap = await getDocs(eventsRef);
    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setEvents(list);
  };

  loadEvents();
}, [teachingMode, id, isGuest]);

useEffect(() => {
  if (!teachingMode) return;

  const loadObjectives = async () => {
    if (isGuest) {
      const list = GUEST_OBJECTIVES[id] || [];
      setObjectives(list.filter(o => !o.archived));
      return;
    }

    const ref = collection(db, "classes", id, "objectives");
    const snap = await getDocs(ref);
    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setObjectives(list.filter(o => !o.archived));
  };

  loadObjectives();
}, [teachingMode, id, isGuest]);

useEffect(() => {
  if (!objectiveManagerOpen) return;

  const loadObjectives = async () => {
    if (isGuest) {
      const list = GUEST_OBJECTIVES[id] || [];
      setObjectives(list.filter(o => !o.archived));
      return;
    }

    const ref = collection(db, "classes", id, "objectives");
    const snap = await getDocs(ref);
    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setObjectives(list.filter(o => !o.archived));
  };

  loadObjectives();
}, [objectiveManagerOpen, id, isGuest]);

useEffect(() => {
  const params = new URLSearchParams(location.search);
  const mode = params.get("mode");
  const objectives = params.get("objectives");

  if (mode === "teaching") {
    setTeachingMode(true);
    setAttendanceMode(false);
    setSelectedStudents([]);
  } else if (!mode) {
    setTeachingMode(false);
    setAttendanceMode(false);
    setSelectedStudents([]);
  }

  if (objectives === "1") {
    setObjectiveManagerOpen(true);
  }
}, [location.search]);

const navParams = useMemo(
  () => new URLSearchParams(location.search),
  [location.search]
);
const isTeachingActive = navParams.get("mode") === "teaching" || teachingMode;
const isObjectivesActive = navParams.get("objectives") === "1" || objectiveManagerOpen;
const isSeatingActive = !isTeachingActive && !isObjectivesActive;



// === compute student stats === //

const studentEventStats = useMemo(() => {
  const stats = {};

  // initialise
  students.forEach(s => {
    stats[s.id] = {
      positive: 0,
      negative: 0,
      objectives: []
    };
  });

  // count events
  events.forEach(e => {
    if (!Array.isArray(e.studentIds)) return;

    e.studentIds.forEach(sid => {
      if (!stats[sid]) return;

      const points = Number(e.points) || 1;
      if (e.type === "positive") stats[sid].positive += points;
      if (e.type === "negative") stats[sid].negative += points;
      if (e.type === "objective" && e.objectiveId) {
        stats[sid].objectives.push(e.objectiveId);
      }
    });
  });

  // keep last 3 objectives
  Object.values(stats).forEach(s => {
    s.objectives = s.objectives.slice(-3);
  });

  return stats;
}, [students, events]);

  const assignedStudentIds = useMemo(() => {
    const ids = new Set();
    Object.values(deskAssignments || {}).forEach((student) => {
      if (student && student.id) ids.add(student.id);
    });
    return ids;
  }, [deskAssignments]);

  const deskAreaBounds = useMemo(() => {
    const desks = Object.values(deskPositions || {});
    if (desks.length === 0) {
      return { width: 1100, height: 1000 };
    }

    let maxX = 0;
    let maxY = 0;
    desks.forEach((desk) => {
      const x = Number(desk.x) || 0;
      const y = Number(desk.y) || 0;
      maxX = Math.max(maxX, x + DESK_WIDTH);
      maxY = Math.max(maxY, y + DESK_HEIGHT);
    });

    return { width: maxX, height: maxY };
  }, [deskPositions]);

  const layoutPreviewBounds = useMemo(() => {
    const desks = Object.values(layoutPreview || {});
    if (desks.length === 0) {
      return { width: 1100, height: 1000 };
    }

    let maxX = 0;
    let maxY = 0;
    desks.forEach((desk) => {
      const x = Number(desk.x) || 0;
      const y = Number(desk.y) || 0;
      maxX = Math.max(maxX, x + DESK_WIDTH);
      maxY = Math.max(maxY, y + DESK_HEIGHT);
    });

    return { width: maxX, height: maxY };
  }, [layoutPreview]);

  useEffect(() => {
    const updateScale = () => {
      const container = deskAreaContainerRef.current;
      if (!container) return;
      const padding = 16;
      const maxHeight = Math.max(Math.round(window.innerHeight * 0.7), 1);
      const availableWidth = Math.max(container.clientWidth - padding, 1);
      const availableHeight = Math.max(maxHeight - padding, 1);
      const scaleX = availableWidth / deskAreaBounds.width;
      const scaleY = availableHeight / deskAreaBounds.height;
      const nextScale = Math.min(scaleX, scaleY);
      setDeskAreaScale(nextScale);

      const contentHeight = deskAreaBounds.height * nextScale + padding;
      setDeskAreaHeight(Math.min(maxHeight, Math.ceil(contentHeight)));
    };

    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, [deskAreaBounds]);




  // ----------------------
  // Firestore helpers
  // ----------------------

  const canWriteToClass = useCallback(() => {
    if (isGuest) return false;
    
    const allowed = Boolean(user?.uid && classTeacherId && user.uid === classTeacherId);
    if (!allowed) {
      console.warn("Blocked write: not class owner", { classId: id, userId: user?.uid });
    }
    return allowed;
  }, [user, classTeacherId, id, isGuest]);

  const getLayoutRef = useCallback(
    (layoutId) => doc(db, "classes", id, "deskLayouts", layoutId),
    [id]
  );

  const loadSavedLayouts = useCallback(async () => {
    if (!id) return;
    
    if (isGuest) {
      // Guests only have the default layout
      setSavedLayouts([]);
      return;
    }

    const layoutsRef = collection(db, "classes", id, "deskLayouts");
    const snapshot = await getDocs(layoutsRef);
    const saved = snapshot.docs
      .map((docSnap) => docSnap.id)
      .filter((layoutId) => layoutId.startsWith("saved-"))
      .map((layoutId) => {
        const match = layoutId.match(/saved-(\d+)/);
        const index = match ? Number(match[1]) : 0;
        return { id: layoutId, index };
      })
      .sort((a, b) => a.index - b.index);
    setSavedLayouts(saved);
  }, [id, isGuest]);

  const loadLayoutPreview = useCallback(async (layoutId, label) => {
    if (!layoutId) return;
    setLayoutPreviewLoading(true);
    setLayoutPreviewLabel(label);
    try {
      const layoutRef = getLayoutRef(layoutId);
      const snapshot = await getDoc(layoutRef);
      const desks = snapshot.exists() ? snapshot.data()?.desks || {} : {};
      setLayoutPreview(Object.keys(desks).length ? desks : null);
    } finally {
      setLayoutPreviewLoading(false);
    }
  }, [getLayoutRef]);

  const saveLayoutById = useCallback(async (layoutId, positions, assignments) => {
    if (!canWriteToClass()) {
      return;
    }
    const layoutRef = getLayoutRef(layoutId);

    const seating = Object.fromEntries(
      Object.entries(assignments).map(([deskId, student]) => [
        deskId,
        student.id
      ])
    );

    const payload = {
      desks: positions,
      seating
    };

    await setDoc(layoutRef, payload, { merge: false });
  }, [canWriteToClass, getLayoutRef]);

  const saveLayout = async (positions, assignments) => {
    await saveLayoutById(currentLayoutId, positions, assignments);
  };

  const loadLayout = useCallback(async (studentsList, layoutId) => {
    if (isGuest) {
      // Load dummy desk layout for guest
      const guestLayout = GUEST_DESK_LAYOUTS[id];
      if (guestLayout && guestLayout[layoutId]) {
        const desks = guestLayout[layoutId].desks || {};
        // Convert array-based layout to object-based if needed
        if (Array.isArray(desks)) {
          const deskObj = {};
          desks.forEach((desk, index) => {
            deskObj[index] = desk;
          });
          setDeskPositions(deskObj);
        } else {
          setDeskPositions(desks);
        }
      } else {
        setDeskPositions(DEFAULT_LAYOUT.desk || {});
      }
      setDeskAssignments({});
      return;
    }

    const layoutRef = getLayoutRef(layoutId);
    const snapshot = await getDoc(layoutRef);

    if (!snapshot.exists()) {
      const fallbackDesks =
        userDefaultDesks && Object.keys(userDefaultDesks).length > 0
          ? userDefaultDesks
          : DEFAULT_LAYOUT.desk || {};

      setDeskPositions(fallbackDesks);
      setDeskAssignments({});
      await saveLayoutById(layoutId, fallbackDesks, {});
      return;
    }

    const data = snapshot.data() || {};
    const desks = data.desks || {};
    const seating = data.seating || {};

    setDeskPositions(desks);

    const restoredAssignments = {};
    for (const [deskId, studentId] of Object.entries(seating)) {
      const student = studentsList.find((s) => s.id === studentId);
      if (student) {
        restoredAssignments[deskId] = student;
      }
    }
    setDeskAssignments(restoredAssignments);
  }, [getLayoutRef, saveLayoutById, userDefaultDesks, id, isGuest]);

  const loadUserDefaultLayout = useCallback(async () => {
    if (!user?.uid) {
      setUserDefaultDesks(null);
      return;
    }

    const userRef = doc(db, "users", user.uid);
    const snapshot = await getDoc(userRef);
    if (!snapshot.exists()) {
      setUserDefaultDesks(null);
      return;
    }

    const data = snapshot.data() || {};
    const defaultLayout = data.defaultDeskLayout;
    const desks = defaultLayout?.desks || null;
    setUserDefaultDesks(desks && Object.keys(desks).length > 0 ? desks : null);
  }, [user]);

  const randomiseSeating = async () => {
    if (students.length === 0) return;

    const deskIds = Object.keys(deskPositions || {});
    const shuffledStudents = [...students].sort(() => Math.random() - 0.5);

    const newAssignments = {};
    deskIds.forEach((deskId, index) => {
      if (shuffledStudents[index]) {
        newAssignments[deskId] = shuffledStudents[index];
      }
    });

    setDeskAssignments(newAssignments);
    await saveLayout(deskPositions, newAssignments);
  };

  const addDesk = async () => {
    if (teachingMode || !canWriteToClass()) return;

    setDeskPositions((prev) => {
      const prevSafe = prev || {};
      const deskId = getNextDeskId(prevSafe);
      const position = findOpenDeskPosition(prevSafe);
      const updated = {
        ...prevSafe,
        [deskId]: { x: position.x, y: position.y, rotation: 0 }
      };

      saveLayout(updated, deskAssignments);
      return updated;
    });
  };

  const removeDesk = async () => {
    if (teachingMode || !canWriteToClass()) return;

    setDeskPositions((prev) => {
      const prevSafe = prev || {};
      const deskIds = Object.keys(prevSafe);
      if (deskIds.length === 0) return prevSafe;

      const targetId = deskIds
        .sort((a, b) => getDeskNumber(b) - getDeskNumber(a))[0];

      const updated = { ...prevSafe };
      delete updated[targetId];

      const updatedAssignments = { ...(deskAssignments || {}) };
      delete updatedAssignments[targetId];
      setDeskAssignments(updatedAssignments);
      saveLayout(updated, updatedAssignments);

      return updated;
    });
  };


    // === Behaviour Event Logging ===
const logEvent = async (eventData) => {
  if (!canWriteToClass()) return;
  const eventsRef = collection(db, "classes", id, "events");
  const points = Number(eventData.points) || 1;

  await addDoc(eventsRef, {
    ...eventData,
    points,
    timestamp: serverTimestamp()
  });

  const dateKey = new Date().toISOString().slice(0, 10);
  const rollupRef = doc(db, "classes", id, "dailyRollups", dateKey);
  const rollupUpdate = {
    date: dateKey,
    updatedAt: serverTimestamp()
  };

  if (eventData.type === "positive") {
    rollupUpdate.positive = increment(points);
  }
  if (eventData.type === "negative") {
    rollupUpdate.negative = increment(points);
  }
  if (eventData.type === "objective") {
    rollupUpdate.objective = increment(1);
    if (eventData.objectiveId) {
      rollupUpdate[`objectivesById.${eventData.objectiveId}`] = increment(1);
    }
  }

  await setDoc(rollupRef, rollupUpdate, { merge: true });
};

const logPositivePoints = async (points) => {
  if (!selectedStudents.length) return;
  await logEvent({
    type: "positive",
    points,
    studentIds: selectedStudents
  });
};

const saveAttendance = async () => {
  if (!canWriteToClass()) return;
  if (attendanceSaving) return;
  setAttendanceSaving(true);

  try {
    const attendanceRef = collection(db, "classes", id, "attendance");
    const presentStudentIds = [...selectedStudents];
    const absentStudentIds = students
      .map((s) => s.id)
      .filter((sid) => !presentStudentIds.includes(sid));

    const dateKey = new Date().toISOString().slice(0, 10);

    await addDoc(attendanceRef, {
      date: dateKey,
      presentStudentIds,
      absentStudentIds,
      timestamp: serverTimestamp()
    });

    logUsageEvent({
      type: "attendance_save",
      name: "attendance",
      classId: id,
      meta: {
        date: dateKey,
        presentCount: presentStudentIds.length,
        absentCount: absentStudentIds.length,
      },
    });

    setSelectedStudents([]);
    setAttendanceMode(false);
  } finally {
    setAttendanceSaving(false);
  }
};




  // ----------------------
  // Load class + students + layout
  // ----------------------

  const loadClassName = useCallback(async () => {
    if (isGuest) {
      // Load dummy class data for guest
      const dummyClass = GUEST_CLASSES.find((cls) => cls.id === id);
      if (dummyClass) {
        setClassName(dummyClass.name);
        setClassTeacherId(dummyClass.teacherId);
      } else {
        setClassName(id);
        setClassTeacherId(null);
      }
      return;
    }

    const classRef = doc(db, "classes", id);
    const snapshot = await getDoc(classRef);
    if (snapshot.exists()) {
      const data = snapshot.data();
      setClassName(data?.name || id);
      setClassTeacherId(data?.teacherId || null);
    } else {
      setClassName(id);
      setClassTeacherId(null);
    }
  }, [id, isGuest]);

  const loadStudents = useCallback(async () => {
    if (isGuest) {
      // Load dummy students for guest
      const dummyStudents = GUEST_STUDENTS[id] || [];
      const studentList = dummyStudents.filter((student) => !student.archived);
      setStudents(studentList);
      return studentList;
    }

    const studentsRef = collection(db, "classes", id, "students");
    const snapshot = await getDocs(studentsRef);

    const studentList = snapshot.docs
      .map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      }))
      .filter((student) => !student.archived);

    setStudents(studentList);
    return studentList;
  }, [id, isGuest]);

  useEffect(() => {
    loadUserDefaultLayout();
  }, [loadUserDefaultLayout]);

  useEffect(() => {
    const loadAll = async () => {
      await loadClassName();
      const studentsList = await loadStudents();
      await loadLayout(studentsList, currentLayoutId);
    };

    if (id) {
      loadAll();
    }
  }, [id, currentLayoutId, loadClassName, loadLayout, loadStudents]);

  useEffect(() => {
    loadSavedLayouts();
  }, [loadSavedLayouts]);

  // ----------------------
  // Teaching Mode: selection logic
  // ----------------------

  const toggleStudentSelection = (studentId) => {
    setSelectedStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  // ----------------------
  // Desk movement & rotation
  // ----------------------

  const handleDeskMove = (deskId, x, y) => {
    if (teachingMode) return;

    const snapped = snapToGrid(x, y);

    setDeskPositions((prev) => {
      const prevSafe = prev || {};
      const current = prevSafe[deskId] || { x: 0, y: 0, rotation: 0 };
      const candidate = findNearestOpenPosition(snapped, prevSafe, deskId);
      if (!candidate) return prevSafe;

      const newPos = { ...current, x: candidate.x, y: candidate.y };

      const updated = {
        ...prevSafe,
        [deskId]: newPos
      };

      saveLayout(updated, deskAssignments);
      return updated;
    });
  };

  const handleDeskRotate = (deskId) => {
    if (teachingMode) return;

    setDeskPositions((prev) => {
      const prevSafe = prev || {};
      const current = prevSafe[deskId] || { x: 0, y: 0, rotation: 0 };

      const updated = {
        ...prevSafe,
        [deskId]: {
          ...current,
          rotation: ((current.rotation || 0) + 90) % 360
        }
      };

      saveLayout(updated, deskAssignments);
      return updated;
    });
  };

  // ----------------------
  // Drop student onto desk
  // ----------------------

  const handleDropStudent = (deskId, student) => {
    if (teachingMode) return;

    setDeskAssignments((prev) => {
      const updated = { ...(prev || {}) };

      Object.keys(updated).forEach((d) => {
        if (updated[d]?.id === student.id) {
          delete updated[d];
        }
      });

      updated[deskId] = student;

      saveLayout(deskPositions, updated);
      return updated;
    });
  };

  // ----------------------
  // Classroom drop zone
  // ----------------------

  const [, dropRef] = useDrop(() => ({
    accept: "DESK",
    drop: (item, monitor) => {
      if (teachingMode) return;

      const delta = monitor.getDifferenceFromInitialOffset();
      if (!delta) return;
      const x = item.x + delta.x / deskAreaScale;
      const y = item.y + delta.y / deskAreaScale;

      handleDeskMove(item.deskId, x, y);
    }
  }));

  // === objectives modal logic ===
const ObjectiveModal = () => {
  if (!objectiveModalOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999 , 
      }}
    >
      <div
        style={{
          background: "white",
          padding: "20px",
          borderRadius: "8px",
          width: "400px" ,
            zIndex: 9999 
        }}
      >
        <h3>Select Learning Objective</h3>

        {objectives.map((obj) => (
          <button
            key={obj.id}
            style={{
              ...buttonStyles.secondary,
              display: "block",
              width: "100%",
              marginBottom: "10px",
              textAlign: "left"
            }}
            onClick={() => {
              logEvent({
                type: "objective",
                objectiveId: obj.id,
                studentIds: selectedStudents
              });
              setSelectedStudents([]);
              setObjectiveModalOpen(false);
            }}
          >
            {obj.title}
          </button>
        ))}

        <button onClick={() => setObjectiveModalOpen(false)} style={buttonStyles.secondary}>Cancel</button>
      </div>
    </div>
  );
};


//=== undo button ==//
const undoLastEvent = async () => {
  const eventsRef = collection(db, "classes", id, "events");
  const snap = await getDocs(eventsRef);

  if (snap.empty) return;

  // Find the most recent event
  const events = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  events.sort((a, b) => b.timestamp?.seconds - a.timestamp?.seconds);

  const last = events[0];
  await deleteDoc(doc(db, "classes", id, "events", last.id));
};


// ==== objective manager modal ===

const ObjectiveManager = () => {
  if (!objectiveManagerOpen) return null;

  const grouped = objectives.reduce((acc, obj) => {
    const cat = obj.category || "Uncategorised";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(obj);
    return acc;
  }, {});

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999
      }}
    >
      <div
        style={{
          background: "white",
          padding: "20px",
          borderRadius: "8px",
          width: "500px",
          maxHeight: "80vh",
          overflowY: "auto"
        }}
      >
        <h2>Manage Learning Objectives</h2>

        <button
          onClick={() => {
            setObjectiveToEdit(null);
            setObjectiveEditorOpen(true);
          }}
          style={{ ...buttonStyles.primary, marginBottom: "20px" }}
        >
          + Add Objective
        </button>

        {Object.entries(grouped).map(([category, items]) => (
          <div key={category} style={{ marginBottom: "20px" }}>
            <h3>{category}</h3>
            {items.map((obj) => (
              <div
                key={obj.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "8px"
                }}
              >
                <span>{obj.title}</span>
                <div>
                  <button
                    onClick={() => {
                      setObjectiveToEdit(obj);
                      setObjectiveEditorOpen(true);
                    }}
                    style={{ ...buttonStyles.secondary, marginRight: "10px" }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={async () => {
                      if (!canWriteToClass()) return;
                      await setDoc(
                        doc(db, "classes", id, "objectives", obj.id),
                        { archived: true },
                        { merge: true }
                      );
                      setObjectives((prev) =>
                        prev.filter((o) => o.id !== obj.id)
                      );
                    }}
                    style={buttonStyles.danger}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        ))}

        <button onClick={() => setObjectiveManagerOpen(false)} style={buttonStyles.secondary}>Close</button>
      </div>
    </div>
  );
};

// === Objective Editor Modal ===
const ObjectiveEditor = () => {
    const [title, setTitle] = useState(objectiveToEdit?.title || "");
  const [category, setCategory] = useState(objectiveToEdit?.category || "");

  if (!objectiveEditorOpen) return null;



  const save = async () => {
    if (!canWriteToClass()) return;
    if (objectiveToEdit) {
      await setDoc(
        doc(db, "classes", id, "objectives", objectiveToEdit.id),
        { title, category },
        { merge: true }
      );
      setObjectives((prev) =>
        prev.map((item) =>
          item.id === objectiveToEdit.id ? { ...item, title, category } : item
        )
      );
    } else {
      const docRef = await addDoc(collection(db, "classes", id, "objectives"), {
        title,
        category,
        archived: false
      });
      setObjectives((prev) => [
        ...prev,
        { id: docRef.id, title, category, archived: false }
      ]);
    }

    setObjectiveEditorOpen(false);
    setObjectiveManagerOpen(true);
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 10000
      }}
    >
      <div
        style={{
          background: "white",
          padding: "20px",
          borderRadius: "8px",
          width: "400px"
        }}
      >
        <h3>{objectiveToEdit ? "Edit Objective" : "Add Objective"}</h3>

        <input
          type="text"
          placeholder="Objective title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          style={{ ...inputStyles.input, width: "100%", marginBottom: "10px" }}
        />

        <input
          type="text"
          placeholder="Category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          style={{ ...inputStyles.input, width: "100%", marginBottom: "20px" }}
        />

        <button onClick={save} style={{ ...buttonStyles.primary, marginRight: "10px" }}>
          Save
        </button>
        <button onClick={() => setObjectiveEditorOpen(false)} style={buttonStyles.secondary}>Cancel</button>
      </div>
    </div>
  );
};



  // ----------------------
  // Render
  // ----------------------

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "40px" }}>
       <ObjectiveModal />
       <ObjectiveManager />
       <ObjectiveEditor />

       <DeskDragPreview
         deskAreaRef={deskAreaContentRef}
         deskAreaScale={deskAreaScale}
         deskPositions={deskPositions}
       />

      
      <div>
        <h1 style={{ margin: "0 0 12px" }}>Class {className}</h1>
        {isGuest && (
          <div style={{ background: "#fff3cd", border: "1px solid #ffc107", padding: "12px", borderRadius: "4px", marginBottom: "16px", fontSize: "0.9rem" }}>
            📋 <strong>Demo Account:</strong> This is a read-only preview. You can view but not save layouts or make changes.
          </div>
        )}
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "20px" }}>
          <Link
            to={`/classes/${id}/seating`}
            style={{
              ...(isSeatingActive ? buttonStyles.primary : buttonStyles.secondary),
              display: "inline-block",
              textDecoration: "none"
            }}
          >
            Seating Plan
          </Link>
          <Link
            to={`/classes/${id}`}
            style={{
              ...buttonStyles.secondary,
              display: "inline-block",
              textDecoration: "none"
            }}
          >
            Classlist
          </Link>
          <Link
            to={`/classes/${id}/seating?mode=teaching`}
            style={{
              ...(isTeachingActive ? buttonStyles.primary : buttonStyles.secondary),
              display: "inline-block",
              textDecoration: "none"
            }}
          >
            Teaching Mode
          </Link>
          <Link
            to={`/class/${id}/analytics`}
            style={{
              ...buttonStyles.secondary,
              display: "inline-block",
              textDecoration: "none"
            }}
          >
            Analytics
          </Link>
          <Link
            to={`/classes/${id}/seating?objectives=1`}
            style={{
              ...(isObjectivesActive ? buttonStyles.primary : buttonStyles.secondary),
              display: "inline-block",
              textDecoration: "none"
            }}
          >
            Manage Objectives
          </Link>
          <Link
            to="/classes"
            style={{
              ...buttonStyles.secondary,
              display: "inline-block",
              textDecoration: "none"
            }}
          >
            Back to Classes
          </Link>
        </div>

        {/* === Teaching Toolbar (placeholder) === */}
        {teachingMode && (
          <div
            style={{
              marginBottom: "20px",
              padding: "10px",
              background: "#f7f7f7",
              border: "1px solid #ccc",
              borderRadius: "6px"
            }}
          >
            <strong>Selected: {selectedStudents.length} students</strong>
            {attendanceMode && (
              <div style={{ marginTop: "6px", fontSize: "0.85rem", color: "#555" }}>
                Attendance mode: select all students who are present, then save.
              </div>
            )}

            <div style={{ marginTop: "10px" }}>

<button
  style={
    selectedStudents.length === 0 || attendanceMode
      ? { ...buttonStyles.success, ...buttonStyles.disabled, marginRight: "10px" }
      : { ...buttonStyles.success, marginRight: "10px" }
  }
  disabled={selectedStudents.length === 0 || attendanceMode}
  onClick={async () => {
    await logPositivePoints(1);
    setSelectedStudents([]);
  }}
>
  + Positive
</button>
              <button
  style={
    selectedStudents.length === 0 || attendanceMode
      ? { ...buttonStyles.success, ...buttonStyles.disabled, marginRight: "10px" }
      : { ...buttonStyles.success, marginRight: "10px" }
  }
  disabled={selectedStudents.length === 0 || attendanceMode}
  onClick={async () => {
    await logPositivePoints(2);
    setSelectedStudents([]);
  }}
>
  +2 Positive
</button>
              <button
  style={
    selectedStudents.length === 0 || attendanceMode
      ? { ...buttonStyles.success, ...buttonStyles.disabled, marginRight: "10px" }
      : { ...buttonStyles.success, marginRight: "10px" }
  }
  disabled={selectedStudents.length === 0 || attendanceMode}
  onClick={async () => {
    await logPositivePoints(5);
    setSelectedStudents([]);
  }}
>
  +5 Positive
</button>
              <button
  style={
    selectedStudents.length === 0 || attendanceMode
      ? { ...buttonStyles.danger, ...buttonStyles.disabled, marginRight: "10px" }
      : { ...buttonStyles.danger, marginRight: "10px" }
  }
  disabled={selectedStudents.length === 0 || attendanceMode}
  onClick={() =>{
    logEvent({
      type: "negative",
      studentIds: selectedStudents
    });
    setSelectedStudents([]);
  }}
>
  – Negative
</button>
              <button
  style={
    selectedStudents.length === 0 || attendanceMode
      ? { ...buttonStyles.secondary, ...buttonStyles.disabled, marginRight: "10px" }
      : { ...buttonStyles.secondary, marginRight: "10px" }
  }
  disabled={selectedStudents.length === 0 || attendanceMode}
  onClick={() => setObjectiveModalOpen(true)}
>
  Learning Objective
</button>

              <button
  style={
    attendanceSaving
      ? { ...buttonStyles.secondary, ...buttonStyles.disabled, marginRight: "10px" }
      : { ...buttonStyles.secondary, marginRight: "10px" }
  }
  disabled={attendanceSaving}
  onClick={() => {
    if (attendanceMode) {
      saveAttendance();
      return;
    }
    setAttendanceMode(true);
    setSelectedStudents([]);
  }}
>
  {attendanceMode ? "Save attendance" : "Attendance"}
</button>

              <button
  disabled={false}
  style={buttonStyles.warning}
  onClick={undoLastEvent}
>
  Undo
</button>
              <button
  type="button"
  style={{ ...buttonStyles.secondary, marginLeft: "10px" }}
  onClick={() => {
    window.open(
      `/classes/${id}/reminders`,
      "student-reminders",
      "width=1200,height=800"
    );
  }}
>
  Student View
</button>
            </div>
          </div>
        )}

        {!teachingMode && (
          <>

            <h2>Students</h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))",
                gap: "10px",
                maxWidth: "1200px"
              }}
            >
              {students.map((s) => (
                <div
                  key={s.id}
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "6px 10px",
                    background: "#f2f2f2",
                    borderRadius: "6px",
                    textAlign: "center"
                  }}
                >
                
                <DraggableStudent
                  student={s}
                  teachingMode={teachingMode}
                  isAssigned={assignedStudentIds.has(s.id)}
                />
                </div>
                
              ))}
            </div>
          </>
        )}
        
      </div>

      {!teachingMode && (
        <div
          style={{
            marginBottom: "0",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap",
            borderBottom: "1px solid #e1e1e1",
            paddingBottom: "0",
            marginTop: "0"
          }}
        >
          <div style={{ display: "inline-block", position: "relative" }}>
            <button
              type="button"
              onClick={() => setLayoutMenuOpen((prev) => !prev)}
              style={{
                background: layoutMenuOpen ? "#fff" : "#f8fafc",
                color: "#1f4f8b",
                border: "1px solid #d9e0ea",
                borderBottom: layoutMenuOpen ? "2px solid #4a90e2" : "1px solid #d9e0ea",
                padding: "0.55rem 0.95rem",
                borderRadius: "10px 10px 0 0",
                fontWeight: 600,
                cursor: "pointer",
                marginBottom: "-1px",
                position: "relative",
                zIndex: 2
              }}
            >
              Layouts
            </button>
            {layoutMenuOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  left: 0,
                  minWidth: "280px",
                  background: "#fff",
                  border: "1px solid #d9e0ea",
                  borderRadius: "10px",
                  boxShadow: "0 12px 28px rgba(15, 23, 42, 0.18)",
                  padding: "10px",
                  zIndex: 20,
                  display: "grid",
                  gap: "8px",
                }}
                onMouseLeave={() => {
                  setLayoutPreview(null);
                  setLayoutPreviewLabel("");
                }}
              >
                <button
                  type="button"
                  onClick={async () => {
                    if (!canWriteToClass()) return;
                    const nextIndex = savedLayouts.length
                      ? Math.max(...savedLayouts.map((item) => item.index)) + 1
                      : 1;
                    const nextId = `saved-${nextIndex}`;
                    await saveLayoutById(nextId, deskPositions, deskAssignments);
                    await loadSavedLayouts();
                    setCurrentLayoutId(nextId);
                  }}
                  style={buttonStyles.secondary}
                >
                  Save current layout
                </button>

                <button
                  type="button"
                  onClick={() => setCurrentLayoutId("default")}
                  style={currentLayoutId === "default" ? buttonStyles.primary : buttonStyles.secondary}
                  onMouseEnter={() => loadLayoutPreview("default", "Default layout")}
                >
                  Default layout
                </button>

                {savedLayouts.length === 0 ? (
                  <div style={{ fontSize: "0.85rem", color: "#667085" }}>
                    No saved layouts yet.
                  </div>
                ) : (
                  savedLayouts.map((layout) => (
                    <div
                      key={layout.id}
                      style={{ display: "flex", alignItems: "center", gap: "8px" }}
                      onMouseEnter={() => loadLayoutPreview(layout.id, `Saved Layout ${layout.index}`)}
                    >
                      <button
                        type="button"
                        onClick={() => setCurrentLayoutId(layout.id)}
                        style={
                          currentLayoutId === layout.id
                            ? { ...buttonStyles.primary, flex: 1 }
                            : { ...buttonStyles.secondary, flex: 1 }
                        }
                      >
                        Saved Layout {layout.index}
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!canWriteToClass()) return;
                          await deleteDoc(doc(db, "classes", id, "deskLayouts", layout.id));
                          await loadSavedLayouts();
                          if (currentLayoutId === layout.id) {
                            setCurrentLayoutId("default");
                          }
                        }}
                        style={{
                          border: "none",
                          background: "transparent",
                          color: "#c62828",
                          fontSize: "18px",
                          cursor: "pointer",
                          padding: "4px 6px",
                        }}
                        title="Delete layout"
                      >
                        🗑
                      </button>
                    </div>
                  ))
                )}

                <div
                  style={{
                    marginTop: "6px",
                    borderTop: "1px solid #eef1f6",
                    paddingTop: "8px",
                  }}
                >
                  <div style={{ fontSize: "0.85rem", color: "#667085", marginBottom: "6px" }}>
                    {layoutPreviewLabel || "Preview"}
                  </div>
                  <div
                    style={{
                      width: "220px",
                      height: "140px",
                      background: "rgba(255, 255, 255, 0.9)",
                      border: "1px solid #d9e0ea",
                      borderRadius: "10px",
                      boxShadow: "0 10px 24px rgba(15, 23, 42, 0.12)",
                      padding: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {layoutPreviewLoading ? (
                      <div style={{ color: "#667085", fontSize: "0.85rem" }}>Loading...</div>
                    ) : layoutPreview && Object.keys(layoutPreview).length > 0 ? (
                      <div
                        style={{
                          position: "relative",
                          width: "100%",
                          height: "100%",
                          background: "rgba(231, 240, 250, 0.35)",
                          borderRadius: "8px",
                          overflow: "hidden"
                        }}
                      >
                        {Object.entries(layoutPreview).map(([deskId, pos]) => {
                          const scale = Math.min(
                            204 / layoutPreviewBounds.width,
                            124 / layoutPreviewBounds.height
                          );
                          return (
                            <div
                              key={deskId}
                              style={{
                                position: "absolute",
                                left: (Number(pos.x) || 0) * scale,
                                top: (Number(pos.y) || 0) * scale,
                                width: DESK_WIDTH * scale,
                                height: DESK_HEIGHT * scale,
                                border: "1px solid rgba(51, 65, 85, 0.5)",
                                borderRadius: "4px",
                                background: "rgba(226, 232, 240, 0.7)"
                              }}
                            />
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ color: "#667085", fontSize: "0.85rem" }}>
                        No layout saved
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          <button
            onClick={randomiseSeating}
            style={{
              background: "#f8fafc",
              color: "#1f4f8b",
              border: "1px solid #d9e0ea",
              borderBottom: "2px solid transparent",
              padding: "0.55rem 0.95rem",
              borderRadius: "10px 10px 0 0",
              fontWeight: 600,
              cursor: "pointer",
              marginBottom: "-1px",
              position: "relative",
              zIndex: 2
            }}
          >
            Randomise Seating
          </button>
          <button
            onClick={addDesk}
            style={{
              background: "#f8fafc",
              color: "#1f4f8b",
              border: "1px solid #d9e0ea",
              borderBottom: "2px solid transparent",
              padding: "0.55rem 0.95rem",
              borderRadius: "10px 10px 0 0",
              fontWeight: 600,
              cursor: "pointer",
              marginBottom: "-1px",
              position: "relative",
              zIndex: 2
            }}
          >
            Add Desk
          </button>
          <button
            onClick={removeDesk}
            style={{
              background: "#f8fafc",
              color: "#1f4f8b",
              border: "1px solid #d9e0ea",
              borderBottom: "2px solid transparent",
              padding: "0.55rem 0.95rem",
              borderRadius: "10px 10px 0 0",
              fontWeight: 600,
              cursor: "pointer",
              marginBottom: "-1px",
              position: "relative",
              zIndex: 2
            }}
          >
            Remove Desk
          </button>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginLeft: "6px",
              color: "#555",
              fontSize: "0.9rem"
            }}
          >
            <span
              style={{
                width: "18px",
                height: "18px",
                borderRadius: "50%",
                border: "1px solid #999",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                fontWeight: 700,
                lineHeight: 1
              }}
            >
              i
            </span>
            <span>Drag desks to move them. Right-click a desk to rotate.</span>
          </div>
        </div>
      )}

      <div
        id="desk-area-container"
        ref={deskAreaContainerRef}
        style={{
          position: "relative",
          width: "100%",
          height: deskAreaHeight || "70vh",
          margin: "-4px auto 0",
          border: "1px solid #e1e1e1",
          borderTop: "none",
          borderRadius: "0 0 10px 10px",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.08)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          background: "#fff",
          boxSizing: "border-box",
          padding: "0"
        }}
      >
        <div
          id="desk-area"
          ref={(node) => {
            deskAreaContentRef.current = node;
            dropRef(node);
          }}
          style={{
            position: "relative",
            padding: "4px",
            width: deskAreaBounds.width * deskAreaScale,
            height: deskAreaBounds.height * deskAreaScale,
            backgroundImage: `
              linear-gradient(to right, #ddd 1px, transparent 1px),
              linear-gradient(to bottom, #ddd 1px, transparent 1px)
            `,
            backgroundSize: `${GRID_SIZE * deskAreaScale}px ${GRID_SIZE * deskAreaScale}px`
          }}
        >
          {Object.entries(deskPositions || {}).map(([deskId, pos]) => (
            <Desk
              key={deskId}
              deskId={deskId}
              x={pos.x}
              y={pos.y}
              rotation={pos.rotation}
              onMove={handleDeskMove}
              onRotate={handleDeskRotate}
              onDropStudent={handleDropStudent}
              assignedStudent={deskAssignments[deskId]}
              teachingMode={teachingMode}
              selected={
                deskAssignments[deskId]
                  ? selectedStudents.includes(deskAssignments[deskId].id)
                  : false
              }
              onSelectStudent={toggleStudentSelection}
              stats={
                teachingMode && deskAssignments[deskId]
                  ? studentEventStats[deskAssignments[deskId].id]
                  : null
              }
              scale={deskAreaScale}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ----------------------
// Drag preview layer
// ----------------------

function DeskDragPreview({ deskAreaRef, deskAreaScale, deskPositions }) {
  const { isDragging, item, delta } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    item: monitor.getItem(),
    delta: monitor.getDifferenceFromInitialOffset(),
  }));

  if (!isDragging || !item || item.deskId == null || !delta) return null;

  const container = deskAreaRef.current;
  if (!container) return null;

  const rect = container.getBoundingClientRect();
  const rawX = item.x + delta.x / deskAreaScale;
  const rawY = item.y + delta.y / deskAreaScale;
  const snapped = snapToGrid(rawX, rawY);
  const previewTarget =
    findNearestOpenPosition(snapped, deskPositions || {}, item.deskId) || snapped;
  const left = rect.left + previewTarget.x * deskAreaScale;
  const top = rect.top + previewTarget.y * deskAreaScale;

  return (
    <div
      style={{
        position: "fixed",
        pointerEvents: "none",
        left,
        top,
        width: `${DESK_WIDTH * deskAreaScale}px`,
        height: `${DESK_HEIGHT * deskAreaScale}px`,
        border: `${Math.max(1, Math.round(2 * deskAreaScale))}px dashed #4da3ff`,
        borderRadius: "6px",
        background: "rgba(77, 163, 255, 0.12)",
        zIndex: 9999,
        boxSizing: "border-box",
      }}
    />
  );
}